import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Send, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS = {
  open: { label: "مفتوح", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  converted: { label: "تم التحويل لفاتورة", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  rejected: { label: "مرفوض", cls: "bg-red-100 text-red-800", Icon: XCircle },
};

export default function Quotations() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ party_id: "", party_name: "", valid_until: "", currency: "SYP", exchange_rate: 1, lines: [], discount_percent: 0, discount_amount: 0, notes: "" });

  const load = async () => {
    try {
      const [q, c, p] = await Promise.all([api.get("/quotations"), api.get("/customers"), api.get("/products")]);
      setItems(q.data); setCustomers(c.data); setProducts(p.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ party_id: "", party_name: "", valid_until: "", currency: "SYP", exchange_rate: 1, lines: [], discount_percent: 0, discount_amount: 0, notes: "" }); setOpen(true); };

  const addLine = () => {
    const p = products[0]; if (!p) return toast.error("لا توجد أصناف");
    setForm(f => ({...f, lines: [...f.lines, { product_id: p.id, product_name: p.name, barcode: p.barcode||"", unit_name: "قطعة", unit_factor: 1, quantity: 1, price: p.sale_price||0, discount: 0, total: p.sale_price||0 }]}));
  };
  const updateLine = (i, patch) => setForm(f => {
    const lines = f.lines.map((l, idx) => {
      if (idx !== i) return l;
      const merged = {...l, ...patch};
      merged.total = Math.max(0, (+merged.quantity || 0) * (+merged.price || 0) - (+merged.discount || 0));
      return merged;
    });
    return {...f, lines};
  });
  const rmLine = (i) => setForm(f => ({...f, lines: f.lines.filter((_, idx) => idx !== i)}));

  const subtotal = form.lines.reduce((s, l) => s + (+l.total || 0), 0);
  const discountTotal = subtotal * (+form.discount_percent || 0)/100 + (+form.discount_amount || 0);
  const total = Math.max(0, subtotal - discountTotal);

  const save = async () => {
    if (!form.party_name.trim() || form.lines.length === 0) return toast.error("الطرف والأصناف مطلوبة");
    try { await api.post("/quotations", form); toast.success("تم إنشاء عرض السعر"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const convert = async (id) => {
    if (!confirm("تحويل هذا العرض إلى فاتورة مبيعات؟")) return;
    try { const r = await api.post(`/quotations/${id}/convert`); toast.success("تم التحويل"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف؟")) return; try { await api.delete(`/quotations/${id}`); load(); } catch(e){ toast.error(formatErr(e)); } };

  return (
    <div className="space-y-4" data-testid="quotations-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><FileText className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">عروض الأسعار</h1>
          <p className="text-sm text-muted-foreground">أنشئ عروض أسعار للعملاء وحوّلها إلى فواتير بنقرة واحدة</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-quotation-btn"><Plus className="w-4 h-4" /> عرض جديد</Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>الرقم</TableHead><TableHead>التاريخ</TableHead><TableHead>العميل</TableHead>
            <TableHead>الإجمالي</TableHead><TableHead>ساري حتى</TableHead>
            <TableHead>الحالة</TableHead><TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(q => {
              const S = STATUS[q.status]; const Icon = S?.Icon;
              return (
                <TableRow key={q.id} data-testid={`quot-row-${q.id}`}>
                  <TableCell className="font-bold">#{q.number}</TableCell>
                  <TableCell className="text-xs">{new Date(q.created_at).toLocaleDateString("ar-EG")}</TableCell>
                  <TableCell>{q.party_name}</TableCell>
                  <TableCell className="font-bold">{q.total.toLocaleString("ar-EG")} {q.currency}</TableCell>
                  <TableCell className="text-xs">{q.valid_until ? new Date(q.valid_until).toLocaleDateString("ar-EG") : "—"}</TableCell>
                  <TableCell><span className={`text-[10px] px-2 py-0.5 rounded font-bold inline-flex items-center gap-1 ${S?.cls}`}>{Icon && <Icon className="w-3 h-3" />}{S?.label}</span></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {q.status === "open" && <Button size="sm" onClick={()=>convert(q.id)} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`convert-${q.id}`}><Send className="w-3 h-3" /> تحويل لفاتورة</Button>}
                      <Button size="sm" variant="ghost" onClick={()=>del(q.id)} className="h-7 text-destructive" data-testid={`del-quot-${q.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">لا توجد عروض أسعار</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quot-dialog">
          <DialogHeader><DialogTitle>عرض سعر جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>العميل</Label>
                <Select value={form.party_id} onValueChange={(v)=>{const c=customers.find(x=>x.id===v); setForm({...form, party_id:v, party_name:c?.name||""})}}>
                  <SelectTrigger data-testid="quot-cust-select"><SelectValue placeholder="اختر عميل أو اترك يدوياً" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                {!form.party_id && <Input value={form.party_name} onChange={(e)=>setForm({...form, party_name:e.target.value})} className="mt-2" placeholder="اسم العميل" data-testid="quot-cust-input" />}
              </div>
              <div>
                <Label>ساري حتى</Label>
                <Input type="date" value={form.valid_until} onChange={(e)=>setForm({...form, valid_until:e.target.value})} data-testid="quot-valid-input" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>الأصناف</Label>
                <Button size="sm" variant="outline" onClick={addLine} className="gap-1 h-7" data-testid="add-quot-line"><Plus className="w-3 h-3" /> صنف</Button>
              </div>
              <div className="border border-border rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>الصنف</TableHead><TableHead className="w-20">الكمية</TableHead>
                    <TableHead className="w-24">السعر</TableHead><TableHead className="w-20">خصم</TableHead>
                    <TableHead className="w-24">الإجمالي</TableHead><TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Select value={l.product_id} onValueChange={(v)=>{const p=products.find(x=>x.id===v); updateLine(i,{product_id:v, product_name:p?.name||"", price:p?.sale_price||0, barcode:p?.barcode||""})}}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell><Input type="number" step="0.01" value={l.quantity} onChange={(e)=>updateLine(i,{quantity:+e.target.value})} className="w-16 h-8" data-testid={`quot-qty-${i}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={l.price} onChange={(e)=>updateLine(i,{price:+e.target.value})} className="w-20 h-8" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={l.discount} onChange={(e)=>updateLine(i,{discount:+e.target.value})} className="w-16 h-8" /></TableCell>
                        <TableCell className="font-bold">{l.total.toFixed(2)}</TableCell>
                        <TableCell><button onClick={()=>rmLine(i)} className="text-destructive" data-testid={`rm-quot-line-${i}`}><Trash2 className="w-3 h-3" /></button></TableCell>
                      </TableRow>
                    ))}
                    {form.lines.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-4 text-xs text-muted-foreground">أضف صنفاً واحداً على الأقل</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 items-end">
              <div><Label>خصم %</Label><Input type="number" step="0.01" value={form.discount_percent} onChange={(e)=>setForm({...form, discount_percent:+e.target.value})} /></div>
              <div><Label>خصم مبلغ</Label><Input type="number" step="0.01" value={form.discount_amount} onChange={(e)=>setForm({...form, discount_amount:+e.target.value})} /></div>
              <div>
                <Label>العملة</Label>
                <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["SYP","USD","TRY","EUR"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Card className="p-3 bg-muted/50">
              <div className="flex justify-between text-sm"><span>الفرعي</span><span className="font-mono">{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span>الخصم</span><span className="font-mono text-red-700">- {discountTotal.toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-primary border-t border-border pt-1 mt-1"><span>الإجمالي</span><span className="font-mono">{total.toFixed(2)} {form.currency}</span></div>
            </Card>
            <div><Label>ملاحظات</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-quot-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
